/**
 * Deep Mode Context Bridge
 * ------------------------
 * Carries the homepage / dashboard Reso conversation into the Framework
 * page (/framework) so the Consciousness Engine can run on the SAME text
 * the user already shared — no re-input, no context loss.
 *
 * Storage: sessionStorage (single tab, cleared on close).
 * Single conversation at a time. Consumed exactly once on the receiving page.
 */
import type { ChatMessage } from './useNavigatorChat';

export const DEEP_CONTEXT_KEY = 'reso_deep_mode_context_v1';
export const DEEP_RESULT_RETURN_KEY = 'reso_deep_mode_result_v1';

export interface DeepModeContext {
  /** All conversation turns up to handoff (used to compose the analysis input). */
  messages: ChatMessage[];
  /** Stage detected in the homepage chat (0–4) if any. */
  detectedStage?: number | null;
  /** Whether the user is the same guest session that started the chat. */
  guestSessionId?: string | null;
  /** ISO timestamp of when the handoff happened. */
  handoffAt: string;
  /** Source surface ('homepage' | 'dashboard'). */
  origin: 'homepage' | 'dashboard';
}

export interface DeepModeResultSummary {
  /** Short human-readable summary written back into the chat. */
  summary: string;
  /** Optional stage / score data carried back into the conversation report. */
  healingStage?: { stage: number; stage_name: string; rationale?: string };
  structuralScores?: Record<string, number>;
  topTherapies?: { name_en: string; name_zh?: string; reason?: string }[];
  /** ISO timestamp of when deep analysis completed. */
  completedAt: string;
}

export function setDeepModeContext(ctx: DeepModeContext) {
  try {
    sessionStorage.setItem(DEEP_CONTEXT_KEY, JSON.stringify(ctx));
  } catch (err) {
    console.warn('[DeepMode] Failed to set context', err);
  }
}

export function consumeDeepModeContext(): DeepModeContext | null {
  try {
    const raw = sessionStorage.getItem(DEEP_CONTEXT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(DEEP_CONTEXT_KEY);
    return JSON.parse(raw) as DeepModeContext;
  } catch {
    return null;
  }
}

export function peekDeepModeContext(): DeepModeContext | null {
  try {
    const raw = sessionStorage.getItem(DEEP_CONTEXT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DeepModeContext;
  } catch {
    return null;
  }
}

/**
 * Compose the conversation transcript into a single text payload that the
 * Consciousness Engine can analyze. Keeps user voice while including light
 * context from AI clarifying questions.
 */
export function composeDeepInput(messages: ChatMessage[]): string {
  const userTurns = messages.filter((m) => m.role === 'user').map((m) => m.content.trim()).filter(Boolean);
  // Prefer user voice — that's what the engine extracts evidence from.
  // If only one short user turn, fall back to including the latest assistant
  // question for context.
  if (userTurns.length === 0) return '';
  const joined = userTurns.join('\n\n');
  if (joined.length >= 60) return joined.slice(0, 3000);

  // Short input → enrich with the most recent AI prompt for context.
  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
  if (!lastAssistant) return joined.slice(0, 3000);
  return `${lastAssistant.content.trim()}\n\n${joined}`.slice(0, 3000);
}

/**
 * After deep analysis, drop a structured summary that the chat surface can
 * read on next mount and append into the conversation as an assistant turn.
 */
export function setDeepModeResult(result: DeepModeResultSummary) {
  try {
    sessionStorage.setItem(DEEP_RESULT_RETURN_KEY, JSON.stringify(result));
  } catch (err) {
    console.warn('[DeepMode] Failed to set result', err);
  }
}

export function consumeDeepModeResult(): DeepModeResultSummary | null {
  try {
    const raw = sessionStorage.getItem(DEEP_RESULT_RETURN_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(DEEP_RESULT_RETURN_KEY);
    return JSON.parse(raw) as DeepModeResultSummary;
  } catch {
    return null;
  }
}
